/**
 * A glyph in a shape, introducing a card, a modal or an empty state.
 *
 * Two faces, both measured. `light-circle` is the tinted disc with a paler
 * halo around it, on the AI replies cards and the mailbox summary cards.
 * `outline-square` is the white chip with a hairline, on modal headers, the
 * integration logo chip and the empty state.
 *
 * The halo is a box-shadow whose colour comes in through a custom property,
 * `--fi-ring`, which each colour sets to its own 50 step. That keeps one
 * shadow rule for every colour and size rather than fifteen arbitrary classes.
 */
import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../lib/cn'

export type FeaturedIconVariant = 'light-circle' | 'outline-square'
export type FeaturedIconColor = 'primary' | 'gray' | 'error' | 'warning' | 'success'
export type FeaturedIconSize = 'sm' | 'md' | 'lg'

/** Box, glyph, halo width and square corner per size. */
const SIZES: Record<FeaturedIconSize, { box: string; glyph: string; ring: number; square: string; px: number }> = {
  sm: { box: 'h-8 w-8', glyph: '[&>svg]:h-4 [&>svg]:w-4', ring: 4, square: 'rounded-lg', px: 32 },
  md: { box: 'h-10 w-10', glyph: '[&>svg]:h-5 [&>svg]:w-5', ring: 6, square: 'rounded-lg', px: 40 },
  lg: { box: 'h-12 w-12', glyph: '[&>svg]:h-6 [&>svg]:w-6', ring: 8, square: 'rounded-[10px]', px: 48 },
}

/*
 * The family's 100 fill, its 50 as the halo and its 600 as the glyph. There was
 * a second set under `dark:` using the soft tokens instead, because a 100 tint
 * glares on a near-black ground. The ground is white, so one set is all there
 * is.
 */
const CIRCLE: Record<FeaturedIconColor, string> = {
  primary:
    'bg-primary-100 text-primary-600 [--fi-ring:var(--c-primary-50)]',
  gray:
    'bg-gray-100 text-gray-600 [--fi-ring:var(--c-gray-50)]',
  error:
    'bg-error-100 text-error-600 [--fi-ring:var(--c-error-50)]',
  warning:
    'bg-warning-100 text-warning-600 [--fi-ring:var(--c-warning-50)]',
  success:
    'bg-success-100 text-success-600 [--fi-ring:var(--c-success-50)]',
}

export interface FeaturedIconProps {
  /** A glyph from `icons.tsx`, or a brand mark for the integration chip. */
  icon: ReactNode
  variant?: FeaturedIconVariant
  color?: FeaturedIconColor
  size?: FeaturedIconSize
  /** Three faint concentric circles behind the shape, for an empty state. */
  rings?: boolean
  className?: string
}

/** The halo, as a style, so its width can follow the size. */
export function featuredRing(size: FeaturedIconSize): CSSProperties {
  return { boxShadow: `0 0 0 ${SIZES[size].ring}px rgb(var(--fi-ring) / var(--fi-ring-a, 1))` }
}

/** The diameters of the three background circles, each a step further out. */
export function ringDiameters(size: FeaturedIconSize): number[] {
  const box = SIZES[size].px
  return [box * 2, box * 3, box * 4]
}

export function FeaturedIcon({
  icon,
  variant = 'light-circle',
  color = 'primary',
  size = 'md',
  rings = false,
  className,
}: FeaturedIconProps) {
  const spec = SIZES[size]
  const shape =
    variant === 'light-circle' ? (
      <span
        aria-hidden="true"
        data-featured={variant}
        style={featuredRing(size)}
        className={cn(
          'relative grid shrink-0 place-items-center rounded-full',
          spec.box,
          spec.glyph,
          CIRCLE[color],
          !rings && className,
        )}
      >
        {icon}
      </span>
    ) : (
      <span
        aria-hidden="true"
        data-featured={variant}
        className={cn(
          'relative grid shrink-0 place-items-center border border-line bg-surface text-ink-secondary shadow-xs',
          spec.box,
          spec.glyph,
          spec.square,
          !rings && className,
        )}
      >
        {icon}
      </span>
    )

  if (!rings) return shape

  return (
    <span data-rings="" className={cn('relative isolate inline-grid shrink-0 place-items-center', className)}>
      {ringDiameters(size).map((diameter, index) => (
        <span
          key={diameter}
          aria-hidden="true"
          style={{ width: diameter, height: diameter, opacity: 1 - index * 0.3 }}
          className="pointer-events-none absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-line"
        />
      ))}
      {shape}
    </span>
  )
}
